import { useMemo } from 'react'
import { addDays, addWeeks } from 'date-fns'
import { cn } from '@/lib/utils'
import { IntuiCalendar, type IntuiCalendarProps } from './IntuiCalendar'

/**
 * Figma (node 21203:53693 — Calendar / Presets):
 *
 * Single card container:
 *   w-[300px], bg-card, border border-border, rounded-xl (14px), pt-3 (12px)
 *
 * Calendar area:
 *   px-3 (12px), day cells 38x38 (md size), w-[266px] grid
 *   bare=true (no inner border/bg since card provides it)
 *
 * Footer (presets area):
 *   border-t border-border (full width separator)
 *   bg-muted/50 (subtle background)
 *   rounded-bl-xl rounded-br-xl (match card corners)
 *   p-3 (12px), gap-2 (8px) between rows
 *
 * Preset buttons:
 *   h-7 (28px), rounded-md (8px), border border-input, bg-background
 *   text-xs font-medium, flex-1 (fill available width)
 *   Two rows: [Today] [Tomorrow] [In 3 days] / [In a week] [In 2 weeks]
 */

export interface CalendarPreset {
  label: string
  date: Date
}

export interface IntuiCalendarPresetsProps extends Omit<IntuiCalendarProps, 'mode' | 'selected' | 'onSelect' | 'size' | 'bare'> {
  /** Currently selected date */
  selected?: Date
  /** Callback when a date is selected */
  onSelect?: (date: Date | undefined) => void
  /** Preset options (default: Today, Tomorrow, In 3 days, In a week, In 2 weeks) */
  presets?: CalendarPreset[]
  /** Additional className for the outer card */
  wrapperClassName?: string
}

function getDefaultPresets(): CalendarPreset[] {
  const now = new Date()
  return [
    { label: 'Today', date: now },
    { label: 'Tomorrow', date: addDays(now, 1) },
    { label: 'In 3 days', date: addDays(now, 3) },
    { label: 'In a week', date: addWeeks(now, 1) },
    { label: 'In 2 weeks', date: addWeeks(now, 2) },
  ]
}

const presetButtonClass = cn(
  'flex-1 inline-flex items-center justify-center',
  'h-7 rounded-md border border-input bg-background',
  'text-xs font-medium text-foreground',
  'hover:bg-accent hover:text-accent-foreground',
  'focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring',
)

function IntuiCalendarPresets({
  selected,
  onSelect,
  presets,
  wrapperClassName,
  className,
  ...calendarProps
}: IntuiCalendarPresetsProps) {
  // Recompute default presets when the date changes (avoids stale "Today" past midnight)
  const todayKey = new Date().toDateString()
  const defaultPresets = useMemo(() => getDefaultPresets(), [todayKey])
  const resolvedPresets = presets ?? defaultPresets
  // Split presets into two rows: first 3 + rest
  const row1 = resolvedPresets.slice(0, 3)
  const row2 = resolvedPresets.slice(3)

  return (
    <div
      className={cn(
        'w-[300px] bg-card border border-border rounded-xl overflow-hidden',
        wrapperClassName,
      )}
    >
      {/* Calendar area — bare (no inner border), md size, extra padding */}
      <div className="pt-3 px-3">
        <IntuiCalendar
          mode="single"
          selected={selected}
          onSelect={onSelect}
          size="md"
          bare
          className={cn('p-0', className)}
          {...calendarProps}
        />
      </div>

      {/* Presets footer — visually attached to calendar */}
      <div className="border-t border-border bg-muted/50 p-3 flex flex-col gap-2">
        {[row1, row2].map((row, rowIdx) =>
          row.length > 0 && (
            <div key={rowIdx} className="flex gap-2">
              {row.map((preset) => (
                <button
                  key={preset.label}
                  type="button"
                  onClick={() => onSelect?.(preset.date)}
                  className={presetButtonClass}
                >
                  {preset.label}
                </button>
              ))}
            </div>
          ),
        )}
      </div>
    </div>
  )
}
IntuiCalendarPresets.displayName = 'IntuiCalendarPresets'

export { IntuiCalendarPresets }
